/**
 * AccountHistorySection — wraps AccountHistoryPanel with an account picker
 * sourced from:
 *   - the live Account Supervision run (simulator accounts)
 *   - a curated set of known OANDA Forex paper accounts (per static project
 *     configuration; not credentials, just IDs the backend already exposes)
 *
 * The picker is purely cosmetic — every account ID is passed to the same
 * read-only backend endpoints, which fail gracefully if the ID is unknown.
 *
 * Read-only. Never sends or accepts broker credentials.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAccountSupervision } from '../../hooks/useAccountSupervision';
import AccountHistoryPanel from './AccountHistoryPanel';

interface AccountOption {
  id: string;
  label: string;
  group: 'forex' | 'simulator' | 'default';
}

// Curated Forex paper accounts. These IDs are public OANDA practice account
// numbers (no credentials), already accepted by the read-only history
// endpoints. They are included so the Risk Center can surface Range Reversion
// (Agro) lockout evidence even when no other discovery source returns them.
const FOREX_PAPER_ACCOUNTS: AccountOption[] = [
  { id: '101-001-39156672-002', label: 'Agro · Range Reversion (Paper)', group: 'forex' },
  { id: '101-001-39156672-001', label: 'RAM · Momentum (Paper)', group: 'forex' },
];

const STORAGE_KEY = 'adversyn:account-history-selected-id';

function loadStoredId(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function storeId(id: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* noop */
  }
}

export default function AccountHistorySection() {
  const { status } = useAccountSupervision();

  const options: AccountOption[] = useMemo(() => {
    const simulatorOptions: AccountOption[] = (status?.accounts ?? []).map((a) => ({
      id: a.account_id_masked,
      label: a.account_label || a.account_id_masked,
      group: 'simulator',
    }));
    // Forex options surface a known-good lockout-history account by default,
    // so the section is meaningful even when supervision hasn't run yet.
    return [...FOREX_PAPER_ACCOUNTS, ...simulatorOptions];
  }, [status]);

  const [selectedId, setSelectedId] = useState<string | null>(() => {
    return loadStoredId() || FOREX_PAPER_ACCOUNTS[0]?.id || null;
  });

  // If the stored ID isn't in the latest options list, keep it anyway — the
  // history endpoints accept any account_id and respond cleanly if unknown.
  useEffect(() => {
    if (!selectedId && options.length > 0) {
      setSelectedId(options[0].id);
    }
  }, [options, selectedId]);

  const selectedLabel = useMemo(() => {
    const found = options.find((o) => o.id === selectedId);
    return found?.label ?? selectedId ?? null;
  }, [options, selectedId]);

  const pick = (id: string) => {
    setSelectedId(id);
    storeId(id);
  };

  return (
    <section
      data-testid="account-history-section"
      aria-label="Account history"
      className="space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] font-black text-[var(--app-text)] uppercase italic">
          Strategy &amp; Account History
        </div>
        <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
          Read-only · No broker calls
        </div>
      </div>

      {options.length === 0 ? (
        <div
          data-testid="account-history-no-options"
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-[11px] font-bold text-amber-700"
        >
          No accounts have been registered for history yet.
        </div>
      ) : (
        <div
          data-testid="account-history-picker"
          className="flex flex-wrap gap-1.5"
          role="tablist"
        >
          {options.map((opt) => {
            const active = opt.id === selectedId;
            return (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`account-history-pick-${opt.group}-${opt.id}`}
                onClick={() => pick(opt.id)}
                className={[
                  'text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors',
                  active
                    ? 'border-sky-500/50 bg-sky-500/15 text-sky-600'
                    : 'border-[var(--card-border)] bg-[var(--card-bg)]/40 text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--card-border)]/30',
                ].join(' ')}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <AccountHistoryPanel accountId={selectedId} accountLabel={selectedLabel} />
    </section>
  );
}
